'use client'

import { useSyncExternalStore, type ReactNode } from 'react'

import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import { SectionHeading, sectionInner, sectionShellAlt } from './SectionHeading'

// Same materials, same object, different results: both panels build a giant martini glass
// out of the same planks and screws. On the left it is botched by a crew with one hand
// screwdriver; on the right it is finished by industrial robot arms, rim walkers and ladder
// crews with power drills. Pure SVG + CSS keyframes; the joint angles are solved in TS
// (2-link IK) so every drill tip lands on its screw. All motion stops under
// prefers-reduced-motion and leaves a sensible static pose.
// ponytail: the 'building' variant (treehouse vs gridshell) is kept only so the founder can
// compare at ?art=building — delete Treehouse/House and the building sides once picked.
const S = 'rgba(255,255,255,0.88)'
const DIM = 'rgba(255,255,255,0.35)'
const FILL = '#161616'
const LIMB = { stroke: S, strokeWidth: 3.4, strokeLinecap: 'round', strokeLinejoin: 'round', fill: 'none' } as const
const MINI = { ...LIMB, strokeWidth: 2.2 } // the small ground crew

// A wood screw, side view: pan head at the origin, threaded tapered shank pointing down.
// `bend` kinks the lower shank (degrees) for the treehouse's mangled ones.
function Screw({ scale = 1, bend = 0 }: { scale?: number; bend?: number }) {
  const b = (bend * Math.PI) / 180
  const p = (x: number, y: number) =>
    y > 13
      ? `${(x * Math.cos(b) - (y - 13) * Math.sin(b)).toFixed(1)} ${(13 + x * Math.sin(b) + (y - 13) * Math.cos(b)).toFixed(1)}`
      : `${x} ${y}`
  return (
    <g transform={`scale(${scale})`}>
      <path
        d={`M-3.2 3 L 3.2 3 L 2.5 13 L ${p(2.2, 22)} L ${p(0, 28)} L ${p(-2.2, 22)} L -2.5 13 Z`}
        fill="rgba(255,255,255,0.18)"
        stroke={S}
        strokeWidth="1"
      />
      {[7, 11, 15, 19, 23].map(y => (
        <path key={y} d={`M${p(-3.4 + y * 0.05, y)} L ${p(3.4 - y * 0.05, y - 2.4)}`} stroke={S} strokeWidth="0.9" />
      ))}
      <path d="M-7 3 L -6 -1.5 Q 0 -4 6 -1.5 L 7 3 Z" fill={FILL} stroke={S} strokeWidth="1.2" />
      <line x1="-3" y1="-0.5" x2="3" y2="-0.5" stroke={S} strokeWidth="1" />
    </g>
  )
}

function Plank({ x, y, w, h = 11, r = 0 }: { x: number; y: number; w: number; h?: number; r?: number }) {
  return (
    <rect
      x={x}
      y={y}
      width={w}
      height={h}
      rx="1.5"
      fill={FILL}
      stroke={S}
      strokeWidth="1.2"
      transform={`rotate(${r} ${x + w / 2} ${y + h / 2})`}
    />
  )
}

// ——— Without Martini ———

// x, y (head), tilt, bend: screws half in, crooked, bent, everywhere
const LOOSE: [number, number, number, number?][] = [
  [182, 214, -28],
  [222, 219, 18],
  [300, 212, -10],
  [166, 226, 34],
  [196, 160, -42],
  [242, 150, 22],
  [224, 178, -20],
  [272, 122, 48],
  [224, 118, -16],
  [352, 184, 58],
  [170, 186, 14],
  [210, 233, -8],
  [252, 237, 26],
  [330, 239, -36],
  [364, 229, 12],
  [178, 236, -60, 30],
  [188, 172, 50, -35],
  [232, 190, -30],
  [262, 198, 70, 28],
  [298, 186, 14],
  [300, 118, -52],
  [346, 150, 30, -40],
  [208, 140, -24],
  [286, 108, 60],
  [334, 138, -70, 32],
]
const DROPPED: [number, number, number, number?][] = [
  [120, 366, 84],
  [360, 368, -78],
  [430, 364, 92],
  [150, 368, -96, 40],
  [238, 370, 80],
  [402, 367, -84, -30],
  [34, 369, -88],
  [78, 366, 96, 24],
  [104, 370, -74],
  [172, 365, 102],
  [196, 369, -82, -28],
  [226, 364, 70],
  [284, 368, -100],
  [312, 370, 86, 34],
  [466, 366, -92],
  [486, 370, 78],
  [528, 367, -70, 30],
]

// A heap of dropped screws on the ground: rows of overlapping shanks, each row shorter than
// the one below, lying mostly sideways with the top ones sticking out at odder angles.
const MOUND_CX = 372
const MOUND = [13, 11, 9, 6, 3].flatMap((count, r) =>
  Array.from({ length: count }, (_, k) => {
    const jitter = ((k * 37 + r * 19) % 5) - 2
    const tilt = 90 + (((k * 53 + r * 29) % (70 + r * 16)) - 35 - r * 8)
    return {
      x: MOUND_CX - ((count - 1) * 7) / 2 + k * 7 + jitter,
      y: 369 - r * 6.5,
      rot: k % 2 ? tilt : -tilt,
    }
  }),
)

// Screws thrown into the air by the ground crew: launched at (x, y), travelling dx sideways,
// peaking h above the hand and landing `drop` below it, tumbling `spin` degrees. The flight
// occupies fractions a..b of the period; the rest is the wind-up, so hands and screws agree.
type Toss = {
  x: number
  y: number
  dx: number
  h: number
  drop: number
  spin: number
  period: number
  delay: number
  a: number
  b: number
}
const TOSS_PERIOD = 2.6
const HANDFUL: Partial<Toss>[] = [
  { x: 502, dx: -72, h: 120, spin: -540, a: 0.1, b: 0.62 },
  { x: 498, dx: -48, h: 95, spin: 420, a: 0.1, b: 0.56 },
  { x: 502, dx: -24, h: 130, spin: -720, a: 0.11, b: 0.66 },
  { x: 498, dx: 2, h: 85, spin: 360, a: 0.1, b: 0.52 },
  { x: 502, dx: 22, h: 110, spin: -480, a: 0.11, b: 0.6 },
  { x: 498, dx: 36, h: 70, spin: 600, a: 0.1, b: 0.5 },
]
const TOSSES: Toss[] = [
  // the frustrated one, both hands up at (±2, -46) from their feet
  ...HANDFUL.map(t => ({ y: 326, drop: 46, period: TOSS_PERIOD, delay: 0, ...t }) as Toss),
  // the searcher flicking the odd one over a shoulder
  { x: 448, y: 370, dx: -34, h: 90, drop: 0, spin: 400, period: 3.3, delay: 0.9, a: 0.3, b: 0.78 },
  { x: 448, y: 370, dx: 12, h: 60, drop: 0, spin: -300, period: 4.1, delay: 2.0, a: 0.3, b: 0.72 },
]
const TOSS_IDS = TOSSES.map((_, i) => `th-toss-${i}`)

function tossCss(id: string, t: Toss) {
  const pose = (f: number) =>
    `translate(${(t.dx * f).toFixed(1)}px, ${(-4 * t.h * f * (1 - f) + t.drop * f * f).toFixed(1)}px) rotate(${(t.spin * f).toFixed(0)}deg)`
  const steps = 10
  const kf = [`0%, ${(t.a * 100).toFixed(2)}% { transform: ${pose(0)}; opacity: 0 }`]
  for (let k = 1; k <= steps; k++) {
    const f = k / steps
    kf.push(`${((t.a + (t.b - t.a) * f) * 100).toFixed(2)}% { transform: ${pose(f)}; opacity: 1 }`)
  }
  kf.push(`${((t.b + 0.04) * 100).toFixed(2)}%, 100% { transform: ${pose(1)}; opacity: 0 }`)
  // base pose is mid-flight so reduced motion freezes the screws in the air
  return `@keyframes ${id} { ${kf.join(' ')} }\n.${id} { transform: ${pose(0.5)}; animation: ${id} ${t.period}s linear ${-t.delay}s infinite; }`
}

function Treehouse() {
  return (
    <svg
      viewBox="0 0 560 400"
      className="h-auto w-full"
      role="img"
      aria-label="A crooked treehouse held together by screws everywhere, driven one at a time by hand, while a crew below hunts through a heap of dropped screws and throws handfuls into the air"
    >
      <line x1="20" y1="372" x2="540" y2="372" stroke={DIM} />
      {/* canopy */}
      <path
        d="M140 120 C 110 70, 170 30, 214 46 C 236 12, 300 10, 326 40 C 372 26, 424 64, 408 106 C 440 130, 418 176, 380 170 C 350 196, 180 196, 160 168 C 118 170, 108 140, 140 120 Z"
        fill="rgba(255,255,255,0.03)"
        stroke={S}
        strokeOpacity="0.5"
        strokeWidth="1.2"
      />
      {/* trunk + branches */}
      <path
        d="M262 372 C 268 320, 258 280, 270 232 L 292 232 C 302 282, 292 326, 304 372 Z"
        fill={FILL}
        stroke={S}
        strokeWidth="1.2"
      />
      <path d="M278 232 C 262 200, 226 184, 196 168" fill="none" stroke={S} strokeWidth="1.4" />
      <path d="M284 232 C 316 196, 350 188, 380 170" fill="none" stroke={S} strokeWidth="1.4" />
      {/* platform: uneven planks */}
      <Plank x={160} y={222} w={100} r={-3} />
      <Plank x={256} y={226} w={112} r={4} />
      {/* walls: mismatched boards, gaps */}
      <Plank x={178} y={150} w={12} h={72} r={-5} />
      <Plank x={206} y={156} w={42} h={62} r={2} />
      <Plank x={256} y={146} w={10} h={78} r={6} />
      <Plank x={286} y={160} w={48} h={60} r={-3} />
      <Plank x={340} y={168} w={12} h={56} r={8} />
      {/* lopsided roof */}
      <Plank x={162} y={132} w={124} h={10} r={-14} />
      <Plank x={266} y={124} w={108} h={10} r={20} />
      {/* rope ladder */}
      <path d="M200 228 C 196 270, 206 310, 198 360" fill="none" stroke={S} strokeOpacity="0.7" />
      <path d="M222 228 C 218 270, 228 310, 220 360" fill="none" stroke={S} strokeOpacity="0.7" />
      {[250, 276, 302, 328, 352].map((y, i) => (
        <line
          key={y}
          x1={199 + (i % 2)}
          y1={y}
          x2={221 - (i % 2)}
          y2={y + (i % 2 ? 2 : -2)}
          stroke={S}
          strokeOpacity="0.7"
        />
      ))}

      {/* the same planks as next door, dumped in a heap */}
      <Plank x={22} y={361} w={70} h={9} r={-3} />
      <Plank x={44} y={351} w={60} h={9} r={5} />
      <Plank x={14} y={343} w={56} h={9} r={-9} />
      <Plank x={56} y={335} w={46} h={9} r={13} />
      <Plank x={94} y={322} w={9} h={50} r={-22} />

      {[...LOOSE, ...DROPPED].map(([x, y, t, bend], i) => (
        <g key={i} transform={`translate(${x} ${y}) rotate(${t})`}>
          <Screw scale={0.8} bend={bend} />
        </g>
      ))}
      {MOUND.map(({ x, y, rot }, i) => (
        <g key={i} transform={`translate(${x.toFixed(1)} ${y}) rotate(${rot})`}>
          <Screw scale={0.6} />
        </g>
      ))}

      <Fixer />
      <Runner />
      <Picker />
      <Searcher />
      <Tosser />

      {TOSSES.map((t, i) => (
        <g key={i} transform={`translate(${t.x} ${t.y})`}>
          <g className={TOSS_IDS[i]}>
            <g transform="translate(0 -7)">
              <Screw scale={0.5} />
            </g>
          </g>
        </g>
      ))}
    </svg>
  )
}

// Standing at the end of the line, hurling a handful of screws up in frustration: both arms
// swing from the waist to straight overhead, the handful leaves as they get there.
function Tosser() {
  return (
    <g transform="translate(500 372)">
      <g {...MINI}>
        <path d="M-6 0 L -3 -9 L 0 -17" />
        <path d="M7 0 L 4 -9 L 0 -17" />
        <path d="M0 -17 L 1 -30" strokeWidth="3.2" />
      </g>
      <circle cx="2" cy="-36" r="3.6" fill={S} />
      <g transform="translate(1 -29)">
        <g className="th-throwA">
          <path {...MINI} d="M0 0 L 7 7 L 14 10" />
        </g>
        <g className="th-throwB">
          <path {...MINI} d="M0 0 L -6 8 L -12 11" />
        </g>
      </g>
    </g>
  )
}

// Kneeling on the platform, both hands on a hand screwdriver, twisting it into a plank one
// screw at a time. The tool, hands and arms rock about the screw tip; the arm roots slide
// under a shoulder disc.
function Fixer({ x = 316, y = 226, rot = 0 }: { x?: number; y?: number; rot?: number }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rot})`}>
      <g className="build-hand-sink">
        <g transform="rotate(4)">
          <Screw scale={0.8} />
        </g>
      </g>
      <g {...LIMB}>
        {/* one knee down, the other foot flat in front */}
        <path d="M48 -2 L 36 0 L 28 -24 L 16 -20 L 14 0 L 8 0" />
        <path d="M28 -24 L 22 -46" strokeWidth="6" />
        <path d="M22 -46 L 22 -51" strokeWidth="2.6" />
      </g>
      <circle cx="22" cy="-57" r="6" fill={S} />
      <circle cx="22" cy="-46" r="5" fill={S} />
      <g transform="rotate(4)">
        <g className="build-hand-tool">
          <g {...LIMB}>
            <path d="M22 -46 L 11 -44 L 2 -42" />
            <path d="M22 -46 L 12 -53 L 2 -50" />
          </g>
          <rect x="-1.1" y="-30" width="2.2" height="28" fill={S} />
          <rect x="-3.5" y="-36" width="7" height="7" rx="1.5" fill={FILL} stroke={S} strokeWidth="1.2" />
          <rect x="-6" y="-62" width="12" height="27" rx="4" fill={FILL} stroke={S} strokeWidth="1.2" />
          <clipPath id="th-grip">
            <rect x="-6" y="-62" width="12" height="27" rx="4" />
          </clipPath>
          <g clipPath="url(#th-grip)">
            <g className="build-hand-grip">
              {[-4.5, -1.5, 1.5, 4.5, 7.5].map(x => (
                <line key={x} x1={x} y1="-58" x2={x} y2="-39" stroke={S} strokeOpacity="0.45" />
              ))}
            </g>
          </g>
          <circle cx="5" cy="-42" r="2.8" fill={S} />
          <circle cx="5" cy="-50" r="2.8" fill={S} />
        </g>
      </g>
    </g>
  )
}

// Runs back and forth along the ground with a screw in hand, turning at each end.
function Runner() {
  const arm = 'M0 0 L 5 6 L 10 3'
  const leg = 'M0 0 L 3 9 L 0 16'
  return (
    <g transform="translate(60 372)">
      <g className="th-run">
        <g className="th-bob">
          <g transform="translate(0 -16)">
            <g className="th-legA">
              <path {...MINI} d={leg} />
            </g>
            <g className="th-legB">
              <path {...MINI} d={leg} />
            </g>
          </g>
          <path {...MINI} d="M0 -16 L 2 -27" strokeWidth="3.2" />
          <circle cx="4" cy="-33" r="3.6" fill={S} />
          <g transform="translate(2 -26)">
            <g className="th-armB">
              <path {...MINI} d={arm} />
            </g>
            <g className="th-armA">
              <path {...MINI} d={arm} />
              <g transform="translate(10 3) rotate(100)">
                <Screw scale={0.4} />
              </g>
            </g>
          </g>
        </g>
      </g>
    </g>
  )
}

// Bends down to a dropped screw, comes up with it, drops it again.
function Picker() {
  return (
    <g transform="translate(138 372)">
      <g {...MINI}>
        <path d="M-3 0 L -2 -8 L 0 -16" />
        <path d="M4 0 L 3 -8 L 0 -16" />
      </g>
      <g transform="translate(0 -16)">
        <g className="th-bend">
          <g {...MINI}>
            <path d="M0 0 L -1 -12" strokeWidth="3.2" />
            <path d="M-1 -10 L -10 -8 L -18 -2" />
          </g>
          <circle cx="-2" cy="-17" r="3.6" fill={S} />
          <g transform="translate(-18 -2) rotate(60)">
            <g className="th-pick">
              <Screw scale={0.45} />
            </g>
          </g>
        </g>
      </g>
    </g>
  )
}

// Crouched, sweeping a hand over the ground looking for the rest.
function Searcher() {
  return (
    <g transform="translate(436 372)">
      <g {...MINI}>
        <path d="M-4 -10 L 5 -9 L 3 0 M-1 0 L 6 0" />
        <path d="M-4 -10 L -9 -4 L -6 0" />
        <path d="M-4 -10 L 2 -24" strokeWidth="3.2" />
      </g>
      <g transform="translate(2 -24)">
        <g className="th-look">
          <circle cx="3" cy="-6" r="3.6" fill={S} />
        </g>
        <g className="th-sweep">
          <path {...MINI} d="M0 0 L 7 10 L 10 22" />
        </g>
      </g>
    </g>
  )
}

// ——— With Martini ———

// The building is a timber gridshell with a Zaha Hadid sweep, made of the treehouse's planks
// and screws: one continuous form that curls up out of the ground on the left, climbs a long
// sweep to a soft ridge, flows down the right into a cantilevered lip and tucks under it
// into a recessed wall. The edge is a laminated rim of plank courses following the outline,
// the body a lattice of plank courses (the top profile repeated downwards) and vertical ribs,
// with a screw head at every joint, over glass. Everything the crew touches is derived from
// the outline: the robot arms drill square into the curved flanks, the roofers walk the
// curved shell, the ladder crews drive the built-up posts. Each half of the crew works in
// its own frame, the left in page coordinates and the right mirrored about x=280 with its
// own curves.
const GROUND = 372
type Pt = { x: number; y: number }
const P = (...xy: number[]): Pt[] =>
  Array.from({ length: xy.length / 2 }, (_, i) => ({ x: xy[2 * i], y: xy[2 * i + 1] }))

// Outline, clockwise from the left foot, as cubic Béziers sharing end points.
const FLANK_L = P(140, 372, 166, 372, 172, 300, 186, 232)
const ROOF_L = P(186, 232, 196, 186, 224, 128, 304, 128)
const SWEEP_R = P(304, 128, 362, 128, 412, 152, 462, 182)
const LIP = P(462, 182, 476, 190, 478, 206, 464, 212)
const SOFFIT = P(464, 212, 444, 216, 414, 222, 396, 230)
const WALL_R = P(396, 230, 380, 236, 380, 320, 396, 372)
const OUTLINE = [FLANK_L, ROOF_L, SWEEP_R, LIP, SOFFIT, WALL_R]

// Lattice: the rim is two plank courses deep; the body courses are the top profile dropped
// by COURSE_DY steps; ribs are vertical planks at RIBS, and the climbers' posts are built up
// from three planks so their screws' shanks stay hidden inside.
const PLANK = 6
const RIM_DEPTH = 16
const COURSE_DY = [30, 62, 94, 126, 158, 190, 222]
const RIBS = [182, 206, 268, 292, 354]
const POST_W = 3 * PLANK
const JAMB_X = 230 // the post the ladder crews drive into, in each side's frame
const LADDER_TOP = { l: 226, r: 270 }
const HEAD_CAP = 4 // how far the pan head rises above the screw origin

const bez = (p: Pt[], t: number): Pt => {
  const u = 1 - t
  const w = [u * u * u, 3 * u * u * t, 3 * u * t * t, t * t * t]
  return { x: w.reduce((a, c, i) => a + c * p[i].x, 0), y: w.reduce((a, c, i) => a + c * p[i].y, 0) }
}
const bezD = (p: Pt[], t: number): Pt => {
  const u = 1 - t
  const d = (k: 'x' | 'y') =>
    3 * (u * u * (p[1][k] - p[0][k]) + 2 * u * t * (p[2][k] - p[1][k]) + t * t * (p[3][k] - p[2][k]))
  return { x: d('x'), y: d('y') }
}
const unit = (v: Pt): Pt => {
  const l = Math.hypot(v.x, v.y)
  return { x: v.x / l, y: v.y / l }
}
// Outward normal of a surface traversed with the building on its right-hand side.
const outward = (u: Pt): Pt => ({ x: u.y, y: -u.x })
const deg = (r: number) => (r * 180) / Math.PI
const f1 = (v: number) => v.toFixed(1)
const mirrorX = (p: Pt): Pt => ({ x: 560 - p.x, y: p.y })

// A workable surface: a cubic sampled to a polyline with cumulative arc length, so targets
// can be "distance along the surface" and screws land exactly on it.
type Surface = {
  len: number
  at: (s: number) => Pt
  frame: (s: number) => { u: Pt; n: Pt }
  sAtY: (y: number) => number
}
function surface(ctrl: Pt[]): Surface {
  const pts = Array.from({ length: 201 }, (_, k) => bez(ctrl, k / 200))
  const cum = pts.reduce<number[]>((acc, p, i) => {
    acc.push(i ? acc[i - 1] + Math.hypot(p.x - pts[i - 1].x, p.y - pts[i - 1].y) : 0)
    return acc
  }, [])
  const len = cum[cum.length - 1]
  const at = (s: number): Pt => {
    const c = Math.max(0, Math.min(len, s))
    let i = 1
    while (i < cum.length - 1 && cum[i] < c) i++
    const f = (c - cum[i - 1]) / (cum[i] - cum[i - 1])
    const a = pts[i - 1]
    const b = pts[i]
    return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f }
  }
  const frame = (s: number) => {
    const a = at(s - 1)
    const b = at(s + 1)
    const u = unit({ x: b.x - a.x, y: b.y - a.y })
    return { u, n: outward(u) }
  }
  // arc position at height y (the flanks rise monotonically)
  const sAtY = (y: number) => {
    let lo = 0
    let hi = len
    for (let k = 0; k < 40; k++) {
      const mid = (lo + hi) / 2
      if (at(mid).y > y) lo = mid
      else hi = mid
    }
    return (lo + hi) / 2
  }
  return { len, at, frame, sAtY }
}

// Per-side geometry in that side's own frame: the flank the robot arm drills into (rising
// from the ground), the roof the roofers climb (rising from its eave), the ladder's top.
type Geo = { flank: Surface; roof: Surface; ladderTop: number }
const GEO_L: Geo = { flank: surface(FLANK_L), roof: surface(ROOF_L), ladderTop: LADDER_TOP.l }
const GEO_R: Geo = {
  flank: surface([...WALL_R].reverse().map(mirrorX)),
  roof: surface([...SWEEP_R].reverse().map(mirrorX)),
  ladderTop: LADDER_TOP.r,
}

// Nominal slope the roofer figure is drawn for; each target rotates the figure by the
// difference between this and the local roof direction.
const SLOPE_DEG = -26

// Robot arm (local frame: base at the origin, building toward +x, y down).
const BASE_X = 22
const SHOULDER = { x: 8, y: -100 }
const L1 = 84
const L2 = 74
const WRIST_LINK = 14
const DRILL_LEN = 52 // bit tip to the back of the body
const TOOL = WRIST_LINK + DRILL_LEN
const ARM_PROUD = 9
const ARM_GAP = 8

// Human figures (roofers on the slope, climbers on ladders): screw proud height, hover gap,
// drill scale, and how far ahead of the feet the screw sits.
const FIG_PROUD = 5
const FIG_GAP = 3
const FIG_DRILL = 0.42
const ROOF_AHEAD = 24 // screw sits this far up-slope from the roofer's feet
const CLIMB_AHEAD = 36 // jamb sits this far in front of the climber's feet
const CLIMB_UP = 52 // screw sits this far above the rung the climber stands on (chest height)
const RUNG = 14 // ladder rung spacing; climber targets must differ by a multiple of it
const LADDER_DX = 3 // ladder centre sits this far in front of the climber's origin

// 2-link planar IK, elbow up: joint angles (degrees, SVG rotate convention) that put the
// wrist at (wx, wy) in the arm's local frame, with the tool pointing along `tool` degrees.
function ik(wx: number, wy: number, tool = 0) {
  const dx = wx - SHOULDER.x
  const dy = wy - SHOULDER.y
  const d = Math.hypot(dx, dy)
  const c = Math.max(-1, Math.min(1, (d * d - L1 * L1 - L2 * L2) / (2 * L1 * L2)))
  const e = Math.acos(c)
  const s = Math.atan2(dy, dx) - Math.atan2(L2 * Math.sin(e), L1 + L2 * Math.cos(e))
  return { s: deg(s), e: deg(e), w: tool - deg(s) - deg(e) }
}

// Each worker's cycle is `n` equal segments. Within segment i: arrive hovering at 0, touch
// the screw head at .10, drive it flush by .32, hold, back off by .48, leave at .56, and
// spend the rest travelling to the next target. 0% and 100% are both "hovering at target 0"
// so the loop is seamless.
const PHASES = [0, 0.1, 0.32, 0.38, 0.48, 0.56] as const
const EASE = ['ease-in', 'linear', 'linear', 'ease-out', 'ease-in-out', 'ease-in-out'] as const
type Stage = 'hover' | 'touch' | 'flush'
const STAGE: Stage[] = ['hover', 'touch', 'flush', 'flush', 'hover', 'hover']

const pct = (n: number, i: number, f: number) => (((i + f) / n) * 100).toFixed(2)

function frames(n: number, valueAt: (i: number, stage: Stage) => string) {
  const out: string[] = []
  for (let i = 0; i < n; i++) {
    PHASES.forEach((f, k) => {
      out.push(`${pct(n, i, f)}% { transform: ${valueAt(i, STAGE[k])}; animation-timing-function: ${EASE[k]} }`)
    })
  }
  out.push(`100% { transform: ${valueAt(0, 'hover')} }`)
  return out.join(' ')
}

function rule(cls: string, base: string, period: number, phase: number) {
  return `.${cls} { transform: ${base}; animation: ${cls} ${period}s linear ${-phase}s infinite; }`
}

type Worker = { id: string; targets: number[]; period: number; phase: number }

// One screw timeline, measured from the moment its driver arrives: proud, driven flush while
// touched, held for most of the cycle, then a gentle fade out and a fade-and-grow back in
// (proud) while the driver is on its way. Each screw i runs the same keyframes, delayed by
// i segments, so none ever pops. The offset is in the screw's own frame (shank along +y),
// so one keyframe set serves every surface angle.
function screwCss({ id, targets, period, phase }: Worker, proudBy: number) {
  const n = targets.length
  const proud = `translateY(${-proudBy}px)`
  const flush = 'translateY(0)'
  const kf = `@keyframes ${id}-k { 0% { transform: ${proud}; opacity: 1 } ${pct(n, 0, PHASES[1])}% { transform: ${proud}; animation-timing-function: linear } ${pct(n, 0, PHASES[2])}% { transform: ${flush} } 74% { transform: ${flush}; opacity: 1; animation-timing-function: ease-in-out } 86% { transform: ${flush}; opacity: 0 } 90% { transform: ${proud} scale(0.6); opacity: 0; animation-timing-function: ease-out } 100% { transform: ${proud}; opacity: 1 } }`
  const rules = targets.map(
    (_, i) =>
      `.${id}-k${i} { transform: ${i === 0 ? flush : proud}; animation: ${id}-k ${period}s linear ${-(phase + (i / n) * period).toFixed(3)}s infinite; }`,
  )
  return [kf, ...rules].join('\n')
}

// Where an arm screw at height y sits on the flank, and the direction the drill points to
// go in square (degrees). The screw is rotated so its shank follows that direction.
function flankScrew(flank: Surface, y: number) {
  const s = flank.sAtY(y)
  const { n } = flank.frame(s)
  const tool = deg(Math.atan2(-n.y, -n.x))
  return { p: flank.at(s), n, tool, rot: tool - 90 }
}

// Robot arm program: three joint keyframe sets + the screws. The tip for each stage sits
// out along the surface normal; the wrist sits TOOL further back along the same line.
function armCss(w: Worker, flank: Surface, baseX: number) {
  const { id, targets, period, phase } = w
  const n = targets.length
  const clear: Record<Stage, number> = {
    hover: ARM_PROUD + HEAD_CAP + ARM_GAP,
    touch: ARM_PROUD + HEAD_CAP,
    flush: HEAD_CAP,
  }
  const pose = (i: number, stage: Stage) => {
    const t = flankScrew(flank, targets[i])
    const back = clear[stage] + TOOL
    return ik(t.p.x + t.n.x * back - baseX, t.p.y + t.n.y * back - GROUND, t.tool)
  }
  const rest = pose(0, 'flush')
  const joints = (['s', 'e', 'w'] as const).map(j => {
    const cls = `${id}-${j}`
    return `@keyframes ${cls} { ${frames(n, (i, st) => `rotate(${pose(i, st)[j].toFixed(2)}deg)`)} }
${rule(cls, `rotate(${rest[j].toFixed(2)}deg)`, period, phase)}`
  })
  return [...joints, screwCss(w, ARM_PROUD)].join('\n')
}

// Human figure program: the whole figure moves between targets (`-m`, translate + rotate so
// it can follow a curved surface); the upper body leans along `dir` (into the work, in the
// figure's own frame) to drive each screw (`-p`).
type Pose = Pt & { rot: number }
function figureCss(w: Worker, pos: (t: number) => Pose, dir: Pt) {
  const { id, targets, period, phase } = w
  const n = targets.length
  const at = (t: number) => {
    const p = pos(t)
    return `translate(${p.x.toFixed(2)}px, ${p.y.toFixed(2)}px) rotate(${p.rot.toFixed(2)}deg)`
  }
  const lean: Record<Stage, number> = { hover: 0, touch: FIG_GAP, flush: FIG_GAP + FIG_PROUD }
  const along = (d: number) => `translate(${(d * dir.x).toFixed(2)}px, ${(d * dir.y).toFixed(2)}px)`
  return [
    `@keyframes ${id}-m { ${frames(n, i => at(targets[i]))} }`,
    rule(`${id}-m`, at(targets[0]), period, phase),
    `@keyframes ${id}-p { ${frames(n, (_, st) => along(lean[st]))} }`,
    rule(`${id}-p`, along(lean.flush), period, phase),
    screwCss(w, FIG_PROUD),
  ].join('\n')
}

// A roofer driving the screw at arc position s stands with their feet on the roof exactly
// ROOF_AHEAD from it (bisected along the polyline, since the chord is shorter than the arc),
// and the whole figure turns so its drill axis is the chord's normal. `ang` is the chord
// direction: the screw is rotated by it so the drill lands square on the head.
function roofPose(roof: Surface, s: number, slope = SLOPE_DEG): Pose & { ang: number } {
  const p = roof.at(s)
  let lo = Math.max(0, s - ROOF_AHEAD - 12)
  let hi = s
  for (let k = 0; k < 30; k++) {
    const mid = (lo + hi) / 2
    const q = roof.at(mid)
    if (Math.hypot(q.x - p.x, q.y - p.y) > ROOF_AHEAD) lo = mid
    else hi = mid
  }
  const f = roof.at((lo + hi) / 2)
  const ang = deg(Math.atan2(p.y - f.y, p.x - f.x))
  return { x: f.x, y: f.y, rot: ang - slope, ang }
}
const climbPos =
  (jambX: number) =>
  (y: number): Pose => ({ x: jambX - CLIMB_AHEAD, y: y + CLIMB_UP, rot: 0 })
const slopeFrame = (slope: number) => {
  const u = { x: Math.cos((slope * Math.PI) / 180), y: Math.sin((slope * Math.PI) / 180) }
  return { u, n: { x: -u.y, y: u.x } }
}
const rooferCss = (w: Worker, roof: Surface, slope: number) =>
  figureCss(w, s => roofPose(roof, s, slope), slopeFrame(slope).n)
const climberCss = (w: Worker, jambX: number) => figureCss(w, climbPos(jambX), { x: 1, y: 0 })

// jambX: the post the ladder crew drives into; baseX: the robot pedestal; slope: the
// nominal slope the roofer figure is drawn for.
type Side = {
  geo: Geo
  arm: Worker
  roofers: Worker[]
  climber: Worker
  jambX: number
  baseX: number
  slope: number
}

// Every worker on its own period and phase so nothing ever moves in lockstep. Arm targets
// are screw heights on the flank; roof targets are distances up the shell from its eave (a
// roofer's screws stay below the next one's feet); climber targets are the screw heights on
// the jamb (rungs sit CLIMB_UP below). The ridge is left clean.
const LEFT: Side = {
  geo: GEO_L,
  jambX: JAMB_X,
  baseX: BASE_X,
  slope: SLOPE_DEG,
  arm: { id: 'arm-l', targets: [256, 274, 292, 310], period: 7.6, phase: 0 },
  roofers: [{ id: 'roof-l', targets: [96, 110, 124], period: 5.4, phase: 1.7 }],
  climber: { id: 'climb-l', targets: [248, 262], period: 6.9, phase: 2.3 },
}
const RIGHT: Side = {
  geo: GEO_R,
  jambX: JAMB_X,
  baseX: BASE_X,
  slope: SLOPE_DEG,
  arm: { id: 'arm-r', targets: [310, 292, 274, 256], period: 8.8, phase: 3.1 },
  roofers: [
    { id: 'roof-r', targets: [54, 26, 40], period: 6.6, phase: 4.2 },
    { id: 'roof-ru', targets: [112, 98, 84], period: 5.0, phase: 2.6 },
  ],
  climber: { id: 'climb-r', targets: [304, 290], period: 7.7, phase: 5.0 },
}
const BUILDING_SIDES = [LEFT, RIGHT]
const MIRROR = 'translate(560 0) scale(-1 1)'

// A cordless drill: bit tip at the origin pointing +x, pistol grip and battery hanging +y.
function Drill({ scale = 1 }: { scale?: number }) {
  const sw = (1.2 / scale).toFixed(2)
  return (
    <g transform={`scale(${scale})`} stroke={S} strokeWidth={sw} strokeLinejoin="round">
      <line x1="0" y1="0" x2="-10" y2="0" strokeWidth={(1.8 / scale).toFixed(2)} />
      <rect x="-18" y="-5" width="8" height="10" rx="1.5" fill={FILL} />
      <rect x="-52" y="-9" width="34" height="18" rx="5" fill={FILL} />
      <line x1="-46" y1="-4" x2="-30" y2="-4" strokeOpacity="0.35" />
      <line x1="-46" y1="0" x2="-30" y2="0" strokeOpacity="0.35" />
      <path d="M-42 8 L -44 27 Q -44 31 -40 31 L -28 31 Q -24 31 -25 27 L -27 8 Z" fill={FILL} />
      <rect x="-48" y="29" width="26" height="9" rx="2" fill={FILL} />
      <path d="M-26 11 L -20 12 L -20 18 L -25 18" fill="none" />
    </g>
  )
}

function joint(r: number) {
  return (
    <>
      <circle r={r} fill={FILL} stroke={S} strokeWidth="1.2" />
      <circle r={r * 0.36} fill="none" stroke={S} strokeWidth="1" />
    </>
  )
}

// An industrial six-axis arm on a pedestal: turntable, shoulder, upper arm, elbow with the
// wrist-motor housing behind it, forearm, wrist, tool flange, drill. Every joint group has
// no transform attribute of its own so the CSS keyframes rotate it about its pivot.
function RobotArm({ id, baseX }: { id: string; baseX: number }) {
  return (
    <g transform={`translate(${baseX} ${GROUND})`}>
      <rect x="-30" y="-6" width="60" height="6" rx="1.5" fill={FILL} stroke={S} strokeWidth="1.2" />
      <rect x="-16" y="-58" width="32" height="52" fill={FILL} stroke={S} strokeWidth="1.2" />
      <rect x="-22" y="-72" width="44" height="14" rx="4" fill={FILL} stroke={S} strokeWidth="1.2" />
      <path
        d={`M-12 -72 L -6 -${-SHOULDER.y + 6} L 22 -${-SHOULDER.y + 6} L 18 -72 Z`}
        fill={FILL}
        stroke={S}
        strokeWidth="1.2"
      />
      <g transform={`translate(${SHOULDER.x} ${SHOULDER.y})`}>
        <g className={`${id}-s`}>
          <path
            d={`M0 -13 L ${L1} -9 L ${L1} 9 L 0 13 Z`}
            fill={FILL}
            stroke={S}
            strokeWidth="1.2"
            strokeLinejoin="round"
          />
          {joint(17)}
          <g transform={`translate(${L1} 0)`}>
            <g className={`${id}-e`}>
              <rect x="-34" y="-13" width="50" height="26" rx="6" fill={FILL} stroke={S} strokeWidth="1.2" />
              <path
                d={`M4 -10 L ${L2} -7 L ${L2} 7 L 4 10 Z`}
                fill={FILL}
                stroke={S}
                strokeWidth="1.2"
                strokeLinejoin="round"
              />
              {joint(11)}
              <g transform={`translate(${L2} 0)`}>
                <g className={`${id}-w`}>
                  <rect x="0" y="-6" width={WRIST_LINK} height="12" rx="2" fill={FILL} stroke={S} strokeWidth="1.2" />
                  {joint(8)}
                  <rect
                    x={WRIST_LINK - 3}
                    y="-11"
                    width="6"
                    height="22"
                    rx="1.5"
                    fill={FILL}
                    stroke={S}
                    strokeWidth="1.2"
                  />
                  <g transform={`translate(${TOOL} 0)`}>
                    <Drill />
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
      </g>
    </g>
  )
}

const FIG_CLEAR = FIG_PROUD + HEAD_CAP + FIG_GAP
// drill-local (-34, 20) is the grip
const GRIP = { x: -34 * FIG_DRILL, y: 20 * FIG_DRILL }

// A roofer standing on the slope, drill pointed perpendicular into the roof ahead of them.
// Positions are derived from the drill so the hands sit on its grip and the bit tip hovers
// over the screw head.
function Roofer({ id, slope }: { id: string; slope: number }) {
  const { u: U, n: N } = slopeFrame(slope)
  const tip = { x: ROOF_AHEAD * U.x - FIG_CLEAR * N.x, y: ROOF_AHEAD * U.y - FIG_CLEAR * N.y }
  // map the grip through the drill's rotated, scaled frame
  const hand = { x: tip.x + GRIP.x * N.x - GRIP.y * N.y, y: tip.y + GRIP.x * N.y + GRIP.y * N.x }
  const foot = 7
  return (
    <g className={`${id}-m`}>
      <g {...LIMB}>
        <path d={`M0 -26 L -6 -13 L ${-foot * U.x} ${-foot * U.y}`} />
        <path d={`M1 -26 L 8 -14 L ${foot * U.x} ${foot * U.y}`} />
      </g>
      <g className={`${id}-p`}>
        <g {...LIMB}>
          <line x1="0" y1="-27" x2="5" y2="-50" strokeWidth="7" />
          <path d={`M5 -48 L ${hand.x.toFixed(1)} ${hand.y.toFixed(1)}`} />
        </g>
        <circle cx="7.5" cy="-58" r="5.2" fill={S} />
        <g transform={`translate(${tip.x.toFixed(2)} ${tip.y.toFixed(2)}) rotate(${(slope + 90).toFixed(2)})`}>
          <Drill scale={FIG_DRILL} />
        </g>
      </g>
    </g>
  )
}

// A climber on a ladder, facing the wall: feet on two rungs, back hand on the rail, the
// drill arm pushing the drill forward at chest height. Only the arm and drill move in the
// push (`-p`); the body, and so the head, stays put, so the drill never crosses the head.
// A shoulder disc hides the arm root sliding along the shoulder.
function Climber({ id }: { id: string }) {
  const tip = { x: CLIMB_AHEAD - FIG_CLEAR, y: -CLIMB_UP }
  const hand = { x: tip.x + GRIP.x, y: tip.y + GRIP.y }
  const rail = LADDER_DX - 8
  return (
    <g className={`${id}-m`}>
      <g {...LIMB}>
        <path d="M1 -40 L -7 -22 L -3 0 M-7 0 L 1 0" />
        <path d={`M1 -40 L 11 -30 L 6 ${-RUNG} M2 ${-RUNG} L 10 ${-RUNG}`} />
        <path d="M1 -40 L -2 -62" strokeWidth="6" />
        <path d="M-2 -62 L -1 -68" strokeWidth="2.6" />
        <path d={`M-2 -62 L ${rail - 4} -54 L ${rail} -44`} />
      </g>
      <circle cx={rail} cy="-44" r="2.4" fill={S} />
      <circle cx="0" cy="-74" r="6" fill={S} />
      <circle cx="-1" cy="-61" r="5" fill={S} />
      <g className={`${id}-p`}>
        <path {...LIMB} d={`M-4 -61 L 6 -56 L ${hand.x.toFixed(1)} ${hand.y.toFixed(1)}`} />
        <g transform={`translate(${tip.x.toFixed(2)} ${tip.y.toFixed(2)})`}>
          <Drill scale={FIG_DRILL} />
        </g>
      </g>
    </g>
  )
}

function Ladder({ top, jambX }: { top: number; jambX: number }) {
  const cx = jambX - CLIMB_AHEAD + LADDER_DX
  return (
    <g stroke={S} strokeOpacity="0.7" strokeWidth="1.2" strokeLinecap="round">
      {[-8, 8].map(dx => (
        <line key={dx} x1={cx + dx} y1={top} x2={cx + dx} y2={GROUND} />
      ))}
      {Array.from({ length: 11 }, (_, k) => GROUND - 2 - k * RUNG)
        .filter(y => y > top)
        .map(y => (
          <line key={y} x1={cx - 8} y1={y} x2={cx + 8} y2={y} />
        ))}
    </g>
  )
}

function AnimatedScrew({ cls, x, y, rot, scale }: { cls: string; x: number; y: number; rot: number; scale?: number }) {
  return (
    <g transform={`translate(${x.toFixed(2)} ${y.toFixed(2)}) rotate(${rot.toFixed(2)})`}>
      <g className={cls}>
        <Screw scale={scale} />
      </g>
    </g>
  )
}

// One side's flank and roof screws, drawn under the shell so the shanks disappear into it.
function ShellScrews({ geo, arm, roofers, slope }: Side) {
  return (
    <>
      {arm.targets.map((y, i) => {
        const t = flankScrew(geo.flank, y)
        return <AnimatedScrew key={y} cls={`${arm.id}-k${i}`} x={t.p.x} y={t.p.y} rot={t.rot} />
      })}
      {roofers.map(r =>
        r.targets.map((s, i) => {
          const p = geo.roof.at(s)
          return (
            <AnimatedScrew
              key={s}
              cls={`${r.id}-k${i}`}
              x={p.x}
              y={p.y}
              rot={roofPose(geo.roof, s, slope).ang}
              scale={0.8}
            />
          )
        }),
      )}
    </>
  )
}

// The climber's screws sit over the lattice and under the post, going into its edge.
function JambScrews({ climber, jambX }: Side) {
  return (
    <>
      {climber.targets.map((y, i) => (
        <AnimatedScrew key={y} cls={`${climber.id}-k${i}`} x={jambX} y={y} rot={-90} scale={0.6} />
      ))}
    </>
  )
}

function Crew({ geo, arm, roofers, climber, jambX, baseX, slope }: Side) {
  return (
    <>
      <Ladder top={geo.ladderTop} jambX={jambX} />
      {roofers.map(r => (
        <Roofer key={r.id} id={r.id} slope={slope} />
      ))}
      <RobotArm id={arm.id} baseX={baseX} />
      <Climber id={climber.id} />
    </>
  )
}

// Each side's layer is drawn once plain and once mirrored.
function Mirrored({ sides = BUILDING_SIDES, layer }: { sides?: Side[]; layer: (side: Side) => ReactNode }) {
  return (
    <>
      {layer(sides[0])}
      <g transform={MIRROR}>{layer(sides[1])}</g>
    </>
  )
}

const GLASS = '#252525' // glazing: opaque so screw shanks under a pier stay hidden
const cubic = (p: Pt[]) => `C ${f1(p[1].x)} ${f1(p[1].y)}, ${f1(p[2].x)} ${f1(p[2].y)}, ${f1(p[3].x)} ${f1(p[3].y)}`

// The whole silhouette, closed along the ground.
const BODY = [`M${FLANK_L[0].x} ${GROUND}`, ...OUTLINE.map(cubic), 'Z'].join(' ')

// The outline sampled with its outward normals, for the contour lines.
const RIM = OUTLINE.flatMap((c, ci) =>
  Array.from({ length: ci === OUTLINE.length - 1 ? 61 : 60 }, (_, k) => {
    const t = k / 60
    const u = unit(bezD(c, t))
    return { p: bez(c, t), u, n: outward(u) }
  }),
)

// Arc length along the rim, for the plank joints.
const RIM_S = RIM.reduce<number[]>((acc, r, i) => {
  acc.push(i ? acc[i - 1] + Math.hypot(r.p.x - RIM[i - 1].p.x, r.p.y - RIM[i - 1].p.y) : 0)
  return acc
}, [])
const rimAt = (s: number) => RIM[RIM_S.findIndex(v => v >= s) < 0 ? RIM.length - 1 : RIM_S.findIndex(v => v >= s)]

// A contour line d inside the outline: the inward offset, keeping only the points that are
// really d from the edge (where the edge curves tighter than d, or two edges close in on
// each other at the lip's root, the raw offset folds back and crosses itself).
function contour(d: number) {
  const out: Pt[] = []
  for (const { p, n } of RIM) {
    const q = { x: p.x - d * n.x, y: p.y - d * n.y }
    if (RIM.every(r => Math.hypot(r.p.x - q.x, r.p.y - q.y) > d - 0.75)) out.push(q)
  }
  return out
}
const poly = (pts: Pt[]) => pts.map((q, i) => `${i ? 'L' : 'M'}${f1(q.x)} ${f1(q.y)}`).join(' ')
// The rim as a filled band: outline out, inner contour back.
const RIM_BAND = `${BODY.slice(0, -1)} ${poly(contour(RIM_DEPTH).reverse()).replace(/^M/, 'L')} Z`
const RIM_SEAM = poly(contour(RIM_DEPTH / 2))
// Plank joints across each rim course, staggered between the two.
const RIM_JOINTS = [0, RIM_DEPTH / 2]
  .flatMap((d0, course) => {
    const out: string[] = []
    for (let s = course ? 18 : 0; s < RIM_S[RIM_S.length - 1]; s += 36) {
      const { p, n } = rimAt(s)
      const d1 = d0 + RIM_DEPTH / 2
      out.push(`M${f1(p.x - d0 * n.x)} ${f1(p.y - d0 * n.y)} L ${f1(p.x - d1 * n.x)} ${f1(p.y - d1 * n.y)}`)
    }
    return out
  })
  .join(' ')

// The top profile (up to the lip's tip) as a polyline; courses are copies dropped by dy.
const TOPLINE = [FLANK_L, ROOF_L, SWEEP_R].flatMap(c => Array.from({ length: 40 }, (_, k) => bez(c, k / 40)))
TOPLINE.push(...Array.from({ length: 19 }, (_, k) => bez(LIP, k / 40)))
const topY = (x: number) => {
  const i = Math.max(
    1,
    TOPLINE.findIndex(p => p.x >= x),
  )
  const a = TOPLINE[i - 1]
  const b = TOPLINE[i]
  return a.y + ((b.y - a.y) * (x - a.x)) / (b.x - a.x || 1)
}
const course = (dy: number) =>
  `${poly(TOPLINE.map(p => ({ x: p.x, y: p.y + dy })))} ${poly([...TOPLINE].reverse().map(p => ({ x: p.x, y: p.y + dy + PLANK }))).replace(/^M/, 'L')} Z`
const COURSES = COURSE_DY.map(course).join(' ')

// Vertical planks; the posts are drawn per side so they sit over the climbers' screws.
function Ribs() {
  return (
    <>
      {RIBS.map(x => (
        <rect key={x} x={x} y="100" width={PLANK} height={GROUND - 100} fill={FILL} stroke={S} strokeOpacity="0.8" />
      ))}
    </>
  )
}
function Post() {
  return (
    <g>
      <rect x={JAMB_X} y="100" width={POST_W} height={GROUND - 100} fill={FILL} stroke={S} strokeWidth="1.2" />
      {[1, 2].map(k => (
        <line
          key={k}
          x1={JAMB_X + k * PLANK}
          y1="100"
          x2={JAMB_X + k * PLANK}
          y2={GROUND}
          stroke={S}
          strokeOpacity="0.5"
        />
      ))}
    </g>
  )
}
// A screw head at every rib-course joint.
const JOINTS = [...RIBS.map(x => x + PLANK / 2), JAMB_X + POST_W / 2, 560 - JAMB_X - POST_W / 2].flatMap(x =>
  COURSE_DY.map(dy => ({ x, y: topY(x) + dy + PLANK / 2 })),
)

// Raw materials, stacked neatly by the robot's pedestal: planks squared up and the next
// screws laid in a row on top.
function NeatStack({ x = 58 }: { x?: number }) {
  return (
    <g>
      {[0, 1, 2, 3].map(r => (
        <Plank key={r} x={x} y={GROUND - 7 * (r + 1)} w={40} h={7} />
      ))}
      {[8, 18, 28, 38].map(dx => (
        <g key={dx} transform={`translate(${x + dx} ${GROUND - 31}) rotate(90)`}>
          <Screw scale={0.5} />
        </g>
      ))}
    </g>
  )
}

function House() {
  return (
    <svg
      viewBox="0 0 560 400"
      className="h-auto w-full"
      role="img"
      aria-label="A sweeping timber gridshell building built from the same planks and screws as the treehouse, with a cantilevered lip, being finished by robot arms, roof crews and ladder crews driving screws with power drills, all at once"
    >
      <line x1="20" y1={GROUND} x2="540" y2={GROUND} stroke={DIM} />

      <Mirrored layer={side => <ShellScrews {...side} />} />

      <clipPath id="shell-body">
        <path d={BODY} />
      </clipPath>
      <path d={BODY} fill={GLASS} />
      <g clipPath="url(#shell-body)">
        <path d={COURSES} fill={FILL} stroke={S} strokeOpacity="0.8" />
        <Ribs />
        {JOINTS.map(({ x, y }) => (
          <circle key={`${x}-${f1(y)}`} cx={x} cy={f1(y)} r="1.3" fill={S} fillOpacity="0.7" />
        ))}
        <Mirrored layer={side => <JambScrews {...side} />} />
        <Mirrored layer={() => <Post />} />
        <path d={RIM_BAND} fill={FILL} stroke="none" />
        <path d={RIM_SEAM} fill="none" stroke={S} strokeOpacity="0.7" />
        <path d={RIM_JOINTS} fill="none" stroke={S} strokeOpacity="0.7" />
      </g>
      <path d={BODY} fill="none" stroke={S} strokeWidth="1.2" strokeLinejoin="round" />

      <NeatStack />
      <Mirrored layer={side => <Crew {...side} />} />
    </svg>
  )
}

// ——— The martini glass, both ways ———

// One silhouette, same scale and position in both panels: a V bowl with a laminated rim,
// a slender stem and a flared foot, built from horizontal plank courses (lamellas) cut to
// the form, with an olive on a pick. The left panel botches it; the right panel finishes it.
const RIM_Y = 100
const RIM_H = 12
const RIM_HW = 130
const BOWL_APEX = 250 // where the V's edges would meet
const STEM_W = 18
const STEM_X = 280 - STEM_W / 2
const bowlHW = (y: number) => (RIM_HW * (BOWL_APEX - y)) / (BOWL_APEX - RIM_Y)
const STEM_TOP = BOWL_APEX - ((STEM_W / 2) * (BOWL_APEX - RIM_Y)) / RIM_HW
const FOOT_TOP = 356
const LAMELLA = 12
const FOOT = `M205 ${GROUND} C 240 ${GROUND}, 258 ${FOOT_TOP + 6}, 262 ${FOOT_TOP} L 298 ${FOOT_TOP} C 302 ${FOOT_TOP + 6}, 320 ${GROUND}, 355 ${GROUND} Z`

// A small round olive with a pimento, outlined like everything else.
function Olive({ x, y, r = 14 }: { x: number; y: number; r?: number }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <circle r={r} fill={FILL} stroke={S} strokeWidth="1.2" />
      <circle cx={-r * 0.3} cy={-r * 0.3} r={r * 0.3} fill={S} fillOpacity="0.7" />
    </g>
  )
}

// ——— Without Martini: the same glass, botched ———

// Deterministic jitter in -8..8 so the mess is the same on every render.
const jit = (k: number, salt: number) => ((k * 7919 + salt * 104729) % 17) - 8
// Courses roughly following the V, each one a little off: shifted, too long or too short,
// tilted. The V still has to read as a glass, so the slop is small except for a few planks.
const BOTCH_COURSES = Array.from({ length: 10 }, (_, k) => {
  const y = RIM_Y + 14 + 12.5 * k
  const hw = bowlHW(y)
  const w = Math.max(30, 2 * hw + jit(k, 2) * 1.2 + (k === 3 ? 48 : 0) + (k === 6 ? -20 : 0))
  return { x: 280 - hw + jit(k, 1) * 0.7 - (k === 3 ? 40 : 0), y: y - 5.5, w, r: jit(k, 3) * 0.5 }
})
// Screws jammed into every course at whatever angle, some bent.
const BOTCH_SCREWS = BOTCH_COURSES.flatMap(({ x, y, w }, k) =>
  (k < 6 ? [0.15, 0.5, 0.85] : [0.3, 0.7]).map((f, i) => ({
    x: x + w * f + jit(k, 4 + i) * 0.6,
    y: y + 5 + jit(k, 7 + i) * 0.4,
    t: jit(k, 10 + i) * 7,
    bend: (k + i) % 3 ? 0 : 30,
  })),
)
// x, y, tilt, bend: the stem, foot and prop get theirs too
const BOTCH_LOOSE: [number, number, number, number?][] = [
  [268, 262, -30],
  [286, 298, 40, -30],
  [274, 330, -15],
  [222, 364, -60],
  [332, 365, 55, 30],
  [256, 350, 20],
  [352, 300, -50],
  [404, 330, 36, -30],
  [432, 354, -70],
  [296, 346, -40],
]

// Where the loose plank rests (on the high end of the leaning rim) and how far it falls.
const FALL_FROM = { x: 190, y: 93 }
const FALL_DROP = GROUND - 6 - FALL_FROM.y

function BotchedGlass() {
  return (
    <svg
      viewBox="0 0 560 400"
      className="h-auto w-full"
      role="img"
      aria-label="A crooked, sagging martini glass sculpture held together by screws jammed in everywhere, driven one at a time by hand, the bowl propped up with a plank, a loose plank sliding off the rim and tumbling to the ground, the olive fallen on the ground, while a crew below hunts through a heap of dropped screws and throws handfuls into the air"
    >
      <line x1="20" y1={GROUND} x2="540" y2={GROUND} stroke={DIM} />

      {/* foot: planks that don't quite stack */}
      <Plank x={198} y={361} w={166} h={10} r={1.5} />
      <Plank x={226} y={351} w={104} h={10} r={-3} />
      <Plank x={250} y={342} w={52} h={9} r={5} />
      {/* stem: two boards leaning apart */}
      <Plank x={264} y={236} w={12} h={116} r={5} />
      <Plank x={282} y={240} w={12} h={110} r={-3} />
      {/* the plank holding the sagging side up */}
      <Plank x={294.5} y={277} w={207} h={10} r={57} />

      {/* the bowl, leaning, with the crew member kneeling on its rim */}
      <g transform={`rotate(6 280 ${BOWL_APEX})`}>
        {BOTCH_COURSES.map(c => (
          <Plank key={c.y} {...c} />
        ))}
        <Plank x={204} y={148} w={110} r={-24} />
        <Plank x={146} y={97} w={268} h={11} r={-2} />
        <Plank x={270} y={94} w={110} h={9} r={3} />
        {BOTCH_SCREWS.map(({ x, y, t, bend }, i) => (
          <g key={i} transform={`translate(${x.toFixed(1)} ${y.toFixed(1)}) rotate(${t})`}>
            <Screw scale={0.8} bend={bend} />
          </g>
        ))}
        <Fixer x={322} y={95} rot={-2} />
      </g>
      {/* one plank nobody fastened: it teeters on the rim, slides off, tumbles down, comes to rest propped on the heap, and is back for the next lap */}
      <g transform={`translate(${FALL_FROM.x} ${FALL_FROM.y})`}>
        <g className="th-fall">
          <Plank x={-32} y={-5} w={64} h={10} />
        </g>
      </g>
      {BOTCH_LOOSE.map(([x, y, t, bend], i) => (
        <g key={i} transform={`translate(${x} ${y}) rotate(${t})`}>
          <Screw scale={0.8} bend={bend} />
        </g>
      ))}

      {/* the olive, on the ground, pick and all */}
      <line x1="196" y1={GROUND - 2} x2="240" y2={GROUND - 9} stroke={S} strokeWidth="2" strokeLinecap="round" />
      <Olive x={178} y={GROUND - 12} r={12} />

      {/* the same planks as next door, dumped in a heap */}
      <Plank x={22} y={361} w={70} h={9} r={-3} />
      <Plank x={44} y={351} w={60} h={9} r={5} />
      <Plank x={14} y={343} w={56} h={9} r={-9} />
      <Plank x={56} y={335} w={46} h={9} r={13} />
      <Plank x={94} y={322} w={9} h={50} r={-22} />

      {DROPPED.map(([x, y, t, bend], i) => (
        <g key={i} transform={`translate(${x} ${y}) rotate(${t})`}>
          <Screw scale={0.8} bend={bend} />
        </g>
      ))}
      <g transform="translate(34 0)">
        {MOUND.map(({ x, y, rot }, i) => (
          <g key={i} transform={`translate(${x.toFixed(1)} ${y}) rotate(${rot})`}>
            <Screw scale={0.6} />
          </g>
        ))}
      </g>

      <Runner />
      <Picker />
      <Searcher />
      <Tosser />

      {TOSSES.map((t, i) => (
        <g key={i} transform={`translate(${t.x} ${t.y})`}>
          <g className={TOSS_IDS[i]}>
            <g transform="translate(0 -7)">
              <Screw scale={0.5} />
            </g>
          </g>
        </g>
      ))}
    </svg>
  )
}

// ——— With Martini: the same glass, finished ———

// The crew works the glass in the same frames as the building: the robot arms drill square
// into the bowl's flanks from below, the rim walkers drive screws down into the laminated
// rim, the ladder crews drive the stem's edges. The glass is symmetric, so both sides share
// one geometry and the right side is simply mirrored.
const GLASS_GEO: Geo = {
  flank: surface(P(STEM_X, STEM_TOP, 231, 193, 190.5, 146.5, 280 - RIM_HW, RIM_Y)),
  roof: surface(P(280 - RIM_HW, RIM_Y, 193, RIM_Y, 237, RIM_Y, 280, RIM_Y)),
  ladderTop: STEM_TOP + 10,
}
const GLASS_BASE_X = 80
const GLASS_L: Side = {
  geo: GLASS_GEO,
  jambX: STEM_X,
  baseX: GLASS_BASE_X,
  slope: 0,
  arm: { id: 'garm-l', targets: [140, 158, 176, 194], period: 7.6, phase: 0 },
  roofers: [{ id: 'grim-l', targets: [64, 88, 112], period: 5.4, phase: 1.7 }],
  climber: { id: 'gclimb-l', targets: [290, 276], period: 6.9, phase: 2.3 },
}
const GLASS_R: Side = {
  geo: GLASS_GEO,
  jambX: STEM_X,
  baseX: GLASS_BASE_X,
  slope: 0,
  arm: { id: 'garm-r', targets: [194, 176, 158, 140], period: 8.8, phase: 3.1 },
  roofers: [{ id: 'grim-r', targets: [112, 88, 64], period: 6.6, phase: 4.2 }],
  climber: { id: 'gclimb-r', targets: [276, 290], period: 7.7, phase: 5.0 },
}
const GLASS_SIDES = [GLASS_L, GLASS_R]

const BOWL = `M${280 - RIM_HW} ${RIM_Y} L ${280 + RIM_HW} ${RIM_Y} L ${f1(280 + STEM_W / 2)} ${f1(STEM_TOP)} L ${STEM_X} ${f1(STEM_TOP)} Z`
// Lamella seams down the bowl, with staggered plank joints and a screw head at each.
const BOWL_COURSES = Array.from(
  { length: Math.ceil((STEM_TOP - RIM_Y - RIM_H) / LAMELLA) },
  (_, k) => RIM_Y + RIM_H + k * LAMELLA,
)
const BOWL_JOINTS = BOWL_COURSES.flatMap((y, k) => {
  const inner = bowlHW(Math.min(y + LAMELLA, STEM_TOP)) - 5
  return Array.from({ length: 13 }, (_, j) => 280 + (j - 6) * 40 + (k % 2) * 20)
    .filter(x => Math.abs(x - 280) < inner)
    .map(x => ({ x, y }))
})

function Glass() {
  return (
    <svg
      viewBox="0 0 560 400"
      className="h-auto w-full"
      role="img"
      aria-label="A tall, perfectly symmetric martini glass sculpture built from the same planks and screws, with a crisp laminated rim, a slender stem, a round foot and an olive on a pick, being finished by robot arms, rim crews and ladder crews driving screws with power drills, all at once"
    >
      <line x1="20" y1={GROUND} x2="540" y2={GROUND} stroke={DIM} />

      <Mirrored sides={GLASS_SIDES} layer={side => <ShellScrews {...side} />} />

      {/* bowl */}
      <clipPath id="glass-bowl">
        <path d={BOWL} />
      </clipPath>
      <path d={BOWL} fill={FILL} />
      <g clipPath="url(#glass-bowl)">
        {BOWL_COURSES.map(y => (
          <line key={y} x1="140" y1={y} x2="420" y2={y} stroke={S} strokeOpacity="0.8" />
        ))}
        {BOWL_JOINTS.map(({ x, y }) => (
          <g key={`${x}-${y}`}>
            <line x1={x} y1={y} x2={x} y2={y + LAMELLA} stroke={S} strokeOpacity="0.7" />
            <circle cx={x + 20} cy={y + LAMELLA / 2} r="1.3" fill={S} fillOpacity="0.7" />
          </g>
        ))}
        {/* laminated rim: two courses, joints staggered */}
        <line x1="140" y1={RIM_Y + RIM_H} x2="420" y2={RIM_Y + RIM_H} stroke={S} strokeWidth="1.2" />
        <line x1="140" y1={RIM_Y + RIM_H / 2} x2="420" y2={RIM_Y + RIM_H / 2} stroke={S} strokeOpacity="0.7" />
        {Array.from({ length: 16 }, (_, j) => 280 - 126 + j * 36).map(x => (
          <g key={x} stroke={S} strokeOpacity="0.7">
            <line x1={x} y1={RIM_Y} x2={x} y2={RIM_Y + RIM_H / 2} />
            <line x1={x - 18} y1={RIM_Y + RIM_H / 2} x2={x - 18} y2={RIM_Y + RIM_H} />
          </g>
        ))}
      </g>
      <path d={BOWL} fill="none" stroke={S} strokeWidth="1.2" strokeLinejoin="round" />

      {/* stem: three planks, the ladder crews' screws going into its edges */}
      <Mirrored sides={GLASS_SIDES} layer={side => <JambScrews {...side} />} />
      <rect
        x={STEM_X}
        y={f1(STEM_TOP)}
        width={STEM_W}
        height={f1(FOOT_TOP - STEM_TOP)}
        fill={FILL}
        stroke={S}
        strokeWidth="1.2"
      />
      {[1, 2].map(k => (
        <line
          key={k}
          x1={STEM_X + k * PLANK}
          y1={f1(STEM_TOP)}
          x2={STEM_X + k * PLANK}
          y2={FOOT_TOP}
          stroke={S}
          strokeOpacity="0.5"
        />
      ))}

      {/* foot: a flared disc of two courses */}
      <clipPath id="glass-foot">
        <path d={FOOT} />
      </clipPath>
      <path d={FOOT} fill={FILL} stroke={S} strokeWidth="1.2" strokeLinejoin="round" />
      <g clipPath="url(#glass-foot)" stroke={S} strokeOpacity="0.7">
        <line x1="200" y1={FOOT_TOP + 8} x2="360" y2={FOOT_TOP + 8} />
        {[232, 268, 304, 340].map(x => (
          <line key={x} x1={x} y1={FOOT_TOP + 8} x2={x} y2={GROUND} />
        ))}
        {[250, 286, 322].map(x => (
          <line key={x} x1={x} y1={FOOT_TOP} x2={x} y2={FOOT_TOP + 8} />
        ))}
      </g>

      {/* the olive on its pick, leaning on the rim */}
      <line x1="233" y1={RIM_Y + 6} x2="305" y2="203" stroke={S} strokeWidth="2" strokeLinecap="round" />
      <Olive x={292} y={186} r={16} />

      <NeatStack x={132} />
      <Mirrored sides={GLASS_SIDES} layer={side => <Crew {...side} />} />
    </svg>
  )
}

const SIDES = [...BUILDING_SIDES, ...GLASS_SIDES]
const WORKERS = SIDES.flatMap(s => [s.arm, ...s.roofers, s.climber])
const ANIM_CLASSES = [
  ...SIDES.flatMap(s => [`.${s.arm.id}-s`, `.${s.arm.id}-e`, `.${s.arm.id}-w`]),
  ...SIDES.flatMap(s => [...s.roofers, s.climber]).flatMap(w => [`.${w.id}-m`, `.${w.id}-p`]),
  ...WORKERS.flatMap(w => w.targets.map((_, i) => `.${w.id}-k${i}`)),
]
const TH_CLASSES = [
  'build-hand-sink',
  'build-hand-tool',
  'build-hand-grip',
  'th-run',
  'th-bob',
  'th-legA',
  'th-legB',
  'th-armA',
  'th-armB',
  'th-bend',
  'th-pick',
  'th-sweep',
  'th-look',
  'th-throwA',
  'th-throwB',
  'th-fall',
  ...TOSS_IDS,
].map(c => `.${c}`)

const KEYFRAMES = `
.drill-panels g[class] { transform-box: view-box; transform-origin: 0 0; }
${SIDES.map(s => armCss(s.arm, s.geo.flank, s.baseX)).join('\n')}
${SIDES.flatMap(s => s.roofers.map(r => rooferCss(r, s.geo.roof, s.slope))).join('\n')}
${SIDES.map(s => climberCss(s.climber, s.jambX)).join('\n')}
@keyframes build-hand-sink { 0% { transform: translateY(0) scale(0.6); opacity: 0 } 8% { transform: translateY(0); opacity: 1 } 82% { transform: translateY(5px); opacity: 1 } 92% { transform: translateY(5px); opacity: 0 } 100% { transform: translateY(0) scale(0.6); opacity: 0 } }
@keyframes build-hand-tool { 0%, 100% { transform: rotate(-4deg) } 50% { transform: rotate(4deg) } }
@keyframes build-hand-grip { from { transform: translateX(0) } to { transform: translateX(-3px) } }
.build-hand-sink { animation: build-hand-sink 9s ease-in-out infinite; }
.build-hand-tool { animation: build-hand-tool 2.6s ease-in-out infinite; }
.build-hand-grip { animation: build-hand-grip 1.3s linear infinite; }
@keyframes th-run { 0% { transform: translateX(0) scaleX(1) } 46% { transform: translateX(340px) scaleX(1) } 50% { transform: translateX(340px) scaleX(-1) } 96% { transform: translateX(0) scaleX(-1) } 100% { transform: translateX(0) scaleX(1) } }
@keyframes th-bob { from { transform: translateY(0) } to { transform: translateY(-2px) } }
@keyframes th-stride { from { transform: rotate(-32deg) } to { transform: rotate(32deg) } }
@keyframes th-bend { 0%, 15% { transform: rotate(-70deg) } 45%, 60% { transform: rotate(0deg) } 90%, 100% { transform: rotate(-70deg) } }
@keyframes th-pick { 0%, 20% { opacity: 0 } 30%, 82% { opacity: 1 } 90%, 100% { opacity: 0 } }
@keyframes th-sweep { from { transform: rotate(-24deg) } to { transform: rotate(18deg) } }
@keyframes th-look { from { transform: translateX(-1.5px) } to { transform: translateX(1.5px) } }
.th-run { animation: th-run 7.4s ease-in-out infinite; }
.th-bob { animation: th-bob 0.19s ease-in-out infinite alternate; }
.th-legA, .th-armB { animation: th-stride 0.38s ease-in-out infinite alternate; }
.th-legB, .th-armA { animation: th-stride 0.38s ease-in-out -0.38s infinite alternate; }
.th-bend { transform: rotate(-70deg); animation: th-bend 3.7s ease-in-out infinite; }
.th-pick { animation: th-pick 3.7s linear infinite; }
.th-sweep { animation: th-sweep 1.1s ease-in-out infinite alternate; }
.th-look { animation: th-look 1.5s ease-in-out infinite alternate; }
@keyframes th-fall {
  0% { transform: translate(0, 0) rotate(4deg); opacity: 0 }
  5%, 18% { transform: translate(0, 0) rotate(4deg); opacity: 1 }
  24% { transform: translate(-2px, 0) rotate(1deg) }
  30% { transform: translate(-4px, 0) rotate(7deg) }
  36% { transform: translate(-10px, 1px) rotate(-2deg); animation-timing-function: ease-in }
  44% { transform: translate(-34px, 10px) rotate(-38deg); animation-timing-function: ease-in }
  60% { transform: translate(-50px, ${FALL_DROP}px) rotate(-196deg); animation-timing-function: ease-out }
  64% { transform: translate(-55px, ${FALL_DROP - 12}px) rotate(-172deg); animation-timing-function: ease-in }
  69%, 90% { transform: translate(-60px, ${FALL_DROP - 8}px) rotate(-158deg); opacity: 1 }
  96%, 100% { transform: translate(-60px, ${FALL_DROP - 8}px) rotate(-158deg); opacity: 0 }
}
.th-fall { transform: rotate(4deg); animation: th-fall 7s linear infinite; }
@keyframes th-throwA { 0% { transform: rotate(10deg) } 8%, 40% { transform: rotate(-125deg) } 46% { transform: rotate(-110deg) } 52% { transform: rotate(-125deg) } 68%, 100% { transform: rotate(10deg) } }
@keyframes th-throwB { 0% { transform: rotate(-10deg) } 8%, 40% { transform: rotate(125deg) } 46% { transform: rotate(110deg) } 52% { transform: rotate(125deg) } 68%, 100% { transform: rotate(-10deg) } }
.th-throwA { transform: rotate(-125deg); animation: th-throwA ${TOSS_PERIOD}s ease-in-out infinite; }
.th-throwB { transform: rotate(125deg); animation: th-throwB ${TOSS_PERIOD}s ease-in-out infinite; }
${TOSSES.map((t, i) => tossCss(TOSS_IDS[i], t)).join('\n')}

@media (prefers-reduced-motion: reduce) {
  ${ANIM_CLASSES.join(', ')}, ${TH_CLASSES.join(', ')} { animation: none; }
}
`

export type DrillArt = 'sculpture' | 'building'

// The two panels on their own, so the comparison pages can show the same picture.
export function DrillPanels({ className = '', variant = 'sculpture' }: { className?: string; variant?: DrillArt }) {
  const building = variant === 'building'
  return (
    <div className={`drill-panels grid gap-4 md:grid-cols-2 ${className}`}>
      <style>{KEYFRAMES}</style>
      {[
        { label: 'AI filmmaking is hard and messy.', panel: building ? <Treehouse /> : <BotchedGlass /> },
        {
          label: 'Martini gives you a professional environment to do your best work.',
          panel: building ? <House /> : <Glass />,
        },
      ].map(({ label, panel }, i) => (
        <figure
          key={label}
          className={`rounded-2xl border p-6 md:p-8 ${
            i === 1 ? 'border-white/20 bg-white/[0.04]' : 'border-white/[0.08] bg-white/[0.015]'
          }`}
        >
          {panel}
          <figcaption
            className={`mt-4 text-balance text-center text-[15px] ${i === 1 ? 'text-white' : 'text-white/45'}`}
          >
            {label}
          </figcaption>
        </figure>
      ))}
    </div>
  )
}

export function DrillSection() {
  const sectionRef = useScrollAnimation<HTMLElement>()
  // ponytail: ?art=building shows the previous art for comparison; delete with the building
  // variant. Read off window so the static page needs no Suspense boundary.
  const variant = useSyncExternalStore<DrillArt>(
    () => () => {},
    () => (new URLSearchParams(window.location.search).get('art') === 'building' ? 'building' : 'sculpture'),
    () => 'sculpture',
  )

  return (
    <section ref={sectionRef} id="power-tool" className={`animate-on-scroll ${sectionShellAlt}`}>
      <div className={sectionInner}>
        <SectionHeading
          lead="Everyone has the same models."
          rest="The workspace makes the difference."
          stacked
          className="mx-auto max-w-[1000px]! text-center"
        />

        <DrillPanels className="mt-14" variant={variant} />
      </div>
    </section>
  )
}
